/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useState } from 'react';
import {
	PillsInput,
	Pill,
	Combobox,
	CheckIcon,
	Group,
	useCombobox,
	PillsInputProps,
} from '@mantine/core';

// Style
import classes from './TagsInput.module.css';
import {
	capitalizeString,
	truncateString,
} from '@utils/functions';
import { Tooltip } from '../Tooltip';
import { useTranslation } from 'react-i18next';
import { NOT_APPLICABLE } from '@config/constants/common';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

type TItem = {
	label: string;
	color: string;
	value: string;
};

type TTagsInputProps = PillsInputProps & {
	data: TItem[];
	placeholder?: string;
	onChange?: (val: string[]) => void;
	value: any;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const TagsInput = (props: TTagsInputProps) => {
	const { t } = useTranslation();
	/* ******************************** Helper ******************************* */
	const combobox = useCombobox({
		onDropdownClose: () => {
			combobox.resetSelectedOption();
		},
		onDropdownOpen: () =>
			combobox.updateSelectedOptionIndex('active'),
	});

	const [search, setSearch] = useState('');

	const handleValueSelect = (_tagLabel: string) => {
		if (props && props.onChange) {
			const foundTag =
				props?.data?.find(
					(item) =>
						item.label.toLowerCase() ==
						_tagLabel.toLowerCase()
				)?.value || NOT_APPLICABLE;

			const currentValue = props.value;

			if (
				currentValue &&
				Array.isArray(currentValue) &&
				!currentValue.find(
					(_tagValue: string) => _tagValue == foundTag
				) &&
				currentValue.length == 2
			) {
				return;
			}
			const body = currentValue.includes(foundTag)
				? currentValue.filter((v: any) => v !== foundTag)
				: [...currentValue, foundTag];
			props.onChange(body);
		}
	};

	const handleValueRemove = (_tagValue: string) => {
		if (props && props.onChange) {
			const currentValue = props.value;
			const body = currentValue.filter(
				(v: string) => v !== _tagValue
			);
			props.onChange(body);
		}
	};

	const values =
		props?.value &&
		props?.value &&
		props.value.map((item: string) => {
			const foundTag = props?.data?.find(
				(item1) => item1.value === item
			);
			return (
				<Pill
					data-testid={'pill-' + item}
					classNames={{
						root: classes.pill,
					}}
					styles={{
						root: {
							backgroundColor:
								'--mantine-color-grey-clair',
						},
					}}
					key={item}
					withRemoveButton
					onRemove={() => handleValueRemove(item)}
				>
					<Tooltip
						isActive={
							props &&
							props?.data &&
							Array.isArray(props?.data) &&
							props.data.length > 0 &&
							foundTag &&
							foundTag.label.length > 10
								? true
								: undefined
						}
						label={capitalizeString(
							props?.data?.find(
								(item1) => item1.value === item
							)?.label || NOT_APPLICABLE
						)}
					>
						<span
							{...(props?.data &&
								Array.isArray(props.data) &&
								props.data.length > 0 && {
									style: {
										color: props.data.find(
											(item1: TItem) =>
												item1.value === item
										)?.color,
									},
								})}
						>
							{'● '}
							<span className={classes.labelText}>
								{capitalizeString(
									truncateString(
										props?.data?.find(
											(item1) => item1.value === item
										)?.label || NOT_APPLICABLE,
										10
									)
								)}
							</span>
						</span>
					</Tooltip>
				</Pill>
			);
		});

	const options =
		props &&
		props.data &&
		Array.isArray(props.data) &&
		props.data
			.filter((item: TItem) =>
				item.value
					.toLowerCase()
					.includes(search.trim().toLowerCase())
			)
			.map((item: TItem) => (
				<Combobox.Option
					data-testid={item.label}
					value={item.label}
					key={item.value}
					active={props.value.includes(item.value)}
				>
					<Group gap="sm">
						{props.value.includes(item.value) ? (
							<CheckIcon size={12} />
						) : null}
						<span>{capitalizeString(item.label)}</span>
					</Group>
				</Combobox.Option>
			));

	/* ******************************** RENDERING ******************************* */
	return (
		<Combobox
			store={combobox}
			onOptionSubmit={handleValueSelect}
		>
			<Combobox.DropdownTarget>
				<PillsInput
					classNames={{
						label: classes.label,
						input: classes.input,
						error: classes.error,
					}}
					{...props}
					data-testid="MPFtczR8_Z5jdHi6B-component"
					onClick={() => combobox.openDropdown()}
				>
					<Pill.Group>
						{values}
						{values.length < 2 && (
							<Combobox.EventsTarget>
								<PillsInput.Field
									className={classes.input}
									data-testid="MPFFczC28_Z5jdHi6B-component"
									onFocus={() => combobox.openDropdown()}
									onBlur={() => {
										combobox.closeDropdown();
									}}
									value={search}
									placeholder={props.placeholder}
									onChange={(event) => {
										combobox.updateSelectedOptionIndex();
										setSearch(event.currentTarget.value);
									}}
									onKeyDown={(event) => {
										if (
											event.key === 'Backspace' &&
											search.length === 0
										) {
											event.preventDefault();
											handleValueRemove(
												props.value[
													props.value.length - 1
												]
											);
										}
									}}
								/>
							</Combobox.EventsTarget>
						)}
					</Pill.Group>
				</PillsInput>
			</Combobox.DropdownTarget>
			<Combobox.Dropdown>
				<Combobox.Options>
					{options && options.length > 0 ? (
						options
					) : (
						<Combobox.Empty>
							{t('global.noData')}
						</Combobox.Empty>
					)}
				</Combobox.Options>
			</Combobox.Dropdown>
		</Combobox>
	);
};
